import { defineStore } from 'pinia'

export const useMessagesStore = defineStore('messages', {
  state: () => ({
    messages: [],
    loading: false
  }),

  getters: {
    activeMessages: (state) => state.messages.filter(m => m.visible),
    hasMessages: (state) => state.messages.some(m => m.visible),
    errorMessages: (state) => state.messages.filter(m => m.type === 'error' && m.visible),
    successMessages: (state) => state.messages.filter(m => m.type === 'success' && m.visible),
    infoMessages: (state) => state.messages.filter(m => m.type === 'info' && m.visible),
    warningMessages: (state) => state.messages.filter(m => m.type === 'warning' && m.visible)
  },

  actions: {
    // Método principal para agregar mensajes
    addMessage(content, type = 'info', duration = 5000) {
      // Una pantalla suele disparar varias peticiones a la vez, así que un
      // backend caído generaría un aviso idéntico por cada una. Si ya hay uno
      // visible con el mismo texto y tipo, se reutiliza en lugar de apilarlo.
      const duplicate = this.messages.find(
        (m) => m.visible && m.type === type && m.content === content
      );
      if (duplicate) {
        return duplicate.id;
      }

      const id = Date.now() + Math.random()
      const message = {
        id,
        content,
        type,
        visible: true,
        timestamp: new Date()
      }
      
      this.messages.push(message)
      
      // Auto-ocultar después de la duración especificada
      if (duration > 0) {
        setTimeout(() => {
          this.hideMessage(id)
        }, duration)
      }
      
      return id
    },

    // Métodos convenientes para cada tipo
    showError(content, duration = 5000) {
      // Si content es un objeto con errores de validación
      if (typeof content === 'object' && !Array.isArray(content)) {
        const errorMessages = []
        for (const messages of Object.values(content)) {
          if (Array.isArray(messages)) {
            errorMessages.push(...messages)
          } else {
            errorMessages.push(messages)
          }
        }
        return this.addMessage(errorMessages.join(' • '), 'error', duration)
      }
      
      return this.addMessage(content, 'error', duration)
    },

    showSuccess(content, duration = 5000) {
      return this.addMessage(content, 'success', duration)
    },

    showInfo(content, duration = 5000) {
      return this.addMessage(content, 'info', duration)
    },

    showWarning(content, duration = 5000) {
      return this.addMessage(content, 'warning', duration)
    },

    // Ocultar un mensaje específico
    hideMessage(id) {
      const message = this.messages.find(m => m.id === id)
      if (message) {
        message.visible = false
      }
    },

    // Limpiar un mensaje específico
    removeMessage(id) {
      const index = this.messages.findIndex(m => m.id === id)
      if (index > -1) {
        this.messages.splice(index, 1)
      }
    },

    // Limpiar todos los mensajes
    clearMessages() {
      this.messages = []
    },

    // Limpiar mensajes por tipo
    clearMessagesByType(type) {
      this.messages = this.messages.filter(m => m.type !== type)
    },

    // Métodos de compatibilidad con stores antiguos
    pushErrors(errors) {
      this.showError(errors)
    },

    pushSuccess(success) {
      this.showSuccess(success)
    },

    setSuccess(message) {
      this.showSuccess(message)
    },

    addError(message) {
      this.showError(message)
    },

    clearErrors() {
      this.clearMessagesByType('error')
    },

    clearSuccess() {
      this.clearMessagesByType('success')
    },

    setLoading(value) {
      this.loading = value
    }
  }
})